import { Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';

/* ================================================================
   BrandLogosV2 — Stacked wordmark, all three words same size
   THE · MOBILITY · GROUP  (uppercase, tracked, three equal lines)
   Star mark carried through; six mark treatments.
   Each option shows all-white and all-amber colour variants.
================================================================ */

const AMBER = '#F59E0B';

// -----------------------------------------------------------------
// Reusable 8-point star mark — all rays computed from the same
// inner/outer radius so every ray is provably identical length
// and every endpoint sits on the same circle. No hand-written
// line coordinates = no way for one ray to be misaligned.
// -----------------------------------------------------------------
const Star = ({ cx = 0, cy = 0, outer = 38, inner = 14, stroke, dot = 5.5 }) => {
  const rays = Array.from({ length: 8 }, (_, i) => {
    const a = (i * Math.PI) / 4; // 0, 45, 90 … degrees
    return {
      x1: cx + inner * Math.cos(a),
      y1: cy + inner * Math.sin(a),
      x2: cx + outer * Math.cos(a),
      y2: cy + outer * Math.sin(a),
    };
  });
  return (
    <g>
      <g stroke={stroke} strokeWidth="3.5" strokeLinecap="round" fill="none">
        {rays.map((r, i) => (
          <line key={i} x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2} />
        ))}
      </g>
      <circle cx={cx} cy={cy} r={dot} fill={stroke} />
    </g>
  );
};

// -----------------------------------------------------------------
// Shared stacked wordmark — three lines, same font-size, generous
// letter-spacing. Passed a fill so we can render all-white / all-amber.
// -----------------------------------------------------------------
const StackedWord = ({ fill = '#FFFFFF', x = 130, y = 60, scale = 1 }) => {
  // baseline positions computed from y so the stack sits nicely
  const line = 34 * scale;
  const size = 22 * scale;
  return (
    <g
      fontFamily="'Inter','Helvetica Neue',Arial,sans-serif"
      fontWeight="600"
      fontSize={size}
      letterSpacing={size * 0.35}
      fill={fill}
    >
      <text x={x} y={y}>THE</text>
      <text x={x} y={y + line}>MOBILITY</text>
      <text x={x} y={y + line * 2}>GROUP</text>
    </g>
  );
};

// -----------------------------------------------------------------
// 01 · Star mark + stacked wordmark
// -----------------------------------------------------------------
const OptStar = ({ size = 240, fill = '#FFFFFF' }) => (
  <svg viewBox="0 0 380 200" width={size} height={size * 200 / 380}>
    <Star cx={70} cy={100} outer={38} inner={14} stroke={fill} />
    <StackedWord fill={fill} x={140} y={78} />
  </svg>
);

// -----------------------------------------------------------------
// 02 · Star mark · centered above the stacked wordmark
// -----------------------------------------------------------------
const OptStarCenter = ({ size = 240, fill = '#FFFFFF' }) => (
  <svg viewBox="0 0 320 260" width={size} height={size * 260 / 320}>
    <Star cx={160} cy={50} outer={32} inner={12} stroke={fill} dot={5} />
    <g
      textAnchor="middle"
      fontFamily="'Inter','Helvetica Neue',Arial,sans-serif"
      fontWeight="600"
      fontSize="22"
      letterSpacing="7"
      fill={fill}
    >
      <text x="160" y="140">THE</text>
      <text x="160" y="176">MOBILITY</text>
      <text x="160" y="212">GROUP</text>
    </g>
  </svg>
);

// -----------------------------------------------------------------
// 03 · Star mark + stack, with a hairline divider between them
// -----------------------------------------------------------------
const OptStarDivider = ({ size = 240, fill = '#FFFFFF' }) => (
  <svg viewBox="0 0 400 200" width={size} height={size * 200 / 400}>
    <Star cx={70} cy={100} outer={38} inner={14} stroke={fill} />
    <line x1="130" y1="50" x2="130" y2="150" stroke={fill} strokeWidth="1" opacity="0.5" />
    <StackedWord fill={fill} x={150} y={78} />
  </svg>
);

// -----------------------------------------------------------------
// 04 · Wordmark only — sophisticated, no mark
// -----------------------------------------------------------------
const OptWordOnly = ({ size = 240, fill = '#FFFFFF' }) => (
  <svg viewBox="0 0 320 200" width={size} height={size * 200 / 320}>
    <g
      textAnchor="middle"
      fontFamily="'Inter','Helvetica Neue',Arial,sans-serif"
      fontWeight="600"
      fontSize="22"
      letterSpacing="7"
      fill={fill}
    >
      <text x="160" y="80">THE</text>
      <text x="160" y="116">MOBILITY</text>
      <text x="160" y="152">GROUP</text>
    </g>
  </svg>
);

// -----------------------------------------------------------------
// 05 · Wordmark with hairlines above and below (editorial rule)
// -----------------------------------------------------------------
const OptRuled = ({ size = 240, fill = '#FFFFFF' }) => (
  <svg viewBox="0 0 320 220" width={size} height={size * 220 / 320}>
    <line x1="60" y1="60" x2="260" y2="60" stroke={fill} strokeWidth="1" />
    <g
      textAnchor="middle"
      fontFamily="'Inter','Helvetica Neue',Arial,sans-serif"
      fontWeight="600"
      fontSize="22"
      letterSpacing="7"
      fill={fill}
    >
      <text x="160" y="98">THE</text>
      <text x="160" y="134">MOBILITY</text>
      <text x="160" y="170">GROUP</text>
    </g>
    <line x1="60" y1="188" x2="260" y2="188" stroke={fill} strokeWidth="1" />
  </svg>
);

// -----------------------------------------------------------------
// 06 · Star mark + stack + small "themobilitygroup.co" tag
// -----------------------------------------------------------------
const OptStarTagged = ({ size = 240, fill = '#FFFFFF' }) => (
  <svg viewBox="0 0 420 220" width={size} height={size * 220 / 420}>
    <Star cx={70} cy={110} outer={38} inner={14} stroke={fill} />
    <StackedWord fill={fill} x={140} y={88} />
    <text
      x={140}
      y={200}
      fontFamily="'JetBrains Mono',ui-monospace,monospace"
      fontSize="10"
      letterSpacing="3"
      fill={fill}
      opacity="0.6"
    >
      THEMOBILITYGROUP.CO
    </text>
  </svg>
);

// ================================================================
// Page
// ================================================================

const OPTIONS = [
  {
    id: 1, key: 'star',
    title: 'Star · horizontal',
    tagline: 'Mark left · stacked wordmark right',
    rationale:
      'The reference structure — 8-point star mark, wordmark stacked to its right in three equal lines. Sophisticated, quiet, unmistakably ours. Best all-round option for headers, letterheads and email signatures.',
    Mark: OptStar,
  },
  {
    id: 2, key: 'star-center',
    title: 'Star · centered',
    tagline: 'Mark above · wordmark centered below',
    rationale:
      'Same star, arranged vertically — reads as a seal, a coat-of-arms, a badge. Sophisticated in a slightly more formal way. Excellent as a stamp on presentations, PDF covers and product packaging.',
    Mark: OptStarCenter,
  },
  {
    id: 3, key: 'star-divider',
    title: 'Star · with divider',
    tagline: 'Hairline rule between mark and words',
    rationale:
      'Star on the left, a hairline vertical rule, then the stacked wordmark. The rule adds architecture — makes the mark feel like a chapter opener in a print journal. Distinctive without being noisy.',
    Mark: OptStarDivider,
  },
  {
    id: 4, key: 'word-only',
    title: 'Wordmark only',
    tagline: 'No mark, no ornament',
    rationale:
      'The most institutional option — three equal, tracked lines. Feels like a hedge fund or an old-line consultancy. The bar for using it is high, but nothing else in the category will look like it.',
    Mark: OptWordOnly,
  },
  {
    id: 5, key: 'ruled',
    title: 'Ruled',
    tagline: 'Hairlines top and bottom',
    rationale:
      'Wordmark contained between two thin rules — Vogue-cover meets FT-editorial. Very sophisticated. Best where you have plenty of vertical space (cover pages, hero sections, physical signage).',
    Mark: OptRuled,
  },
  {
    id: 6, key: 'star-tagged',
    title: 'Star · with URL',
    tagline: 'Mark, stack, then the domain in mono',
    rationale:
      'Same as Option 1 but with themobilitygroup.co as a small monospace tag underneath the stack. Best where the logo also needs to communicate "we exist online" — footers, email signatures, business cards.',
    Mark: OptStarTagged,
  },
];

const BrandLogosV2 = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="px-6 md:px-12 lg:px-16 h-14 flex items-center justify-between">
          <Link
            to="/brand"
            data-testid="v2-back-to-brand"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-amber inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> brand · concepts
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hidden sm:inline">
            v2 · stacked wordmark
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12 md:pb-16 border-b border-white/10">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-3">
          Brand · v2 · stacked wordmark
        </div>
        <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-4xl leading-[1.02]">
          THE · MOBILITY · GROUP
          <br />
          <span className="text-amber">Three equal lines. Two colours.</span>
        </h1>
        <p className="mt-6 max-w-3xl text-base md:text-lg text-zinc-300 leading-relaxed">
          All three words the same size, stacked cleanly on each other. Every
          option below is shown in two colourways — all-white on dark, and
          all-amber on dark — so you can see which reads more sophisticated for
          you. Star mark preserved across the marked options.
        </p>
      </section>

      {/* Options */}
      <section className="px-6 md:px-12 lg:px-16 py-16 space-y-10">
        {OPTIONS.map((opt) => (
          <article
            key={opt.id}
            data-testid={`brand-option-${opt.key}`}
            className="border border-white/10 bg-ink-800 overflow-hidden"
          >
            {/* Two colourway previews, side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* All white */}
              <div className="relative bg-ink-900 min-h-[300px] p-10 flex items-center justify-center border-r border-white/10">
                <div className="absolute inset-0 grid-lines opacity-40" />
                <div className="relative">
                  <opt.Mark size={460} fill="#FFFFFF" />
                </div>
                <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-amber">
                  / {String(opt.id).padStart(2, '0')} · A
                </div>
                <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  All white
                </div>
              </div>
              {/* All amber */}
              <div className="relative bg-black min-h-[300px] p-10 flex items-center justify-center">
                <div className="absolute inset-0 grid-lines opacity-40" />
                <div className="absolute inset-0 radial-glow-amber opacity-30" />
                <div className="relative">
                  <opt.Mark size={460} fill={AMBER} />
                </div>
                <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-amber">
                  / {String(opt.id).padStart(2, '0')} · B
                </div>
                <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.2em] text-amber/70">
                  All amber
                </div>
              </div>
            </div>

            {/* Meta row */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-8 border-t border-white/10">
              <div className="lg:col-span-5">
                <h3 className="font-display text-2xl md:text-3xl text-white">
                  {opt.title}
                </h3>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber mt-1">
                  {opt.tagline}
                </div>
                <p className="mt-4 text-zinc-400 text-sm leading-relaxed">
                  {opt.rationale}
                </p>
              </div>

              <div className="lg:col-span-5">
                {/* Small variations: on white, on cream, favicon-size */}
                <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                  <div className="bg-white p-4 flex items-center justify-center min-h-[100px]">
                    <opt.Mark size={140} fill="#0A0A0A" />
                  </div>
                  <div className="bg-white p-4 flex items-center justify-center min-h-[100px]">
                    <opt.Mark size={140} fill={AMBER} />
                  </div>
                  <div className="bg-ink-900 p-4 flex items-center justify-center min-h-[100px]">
                    <opt.Mark size={80} fill="#FFFFFF" />
                  </div>
                </div>
                <div className="grid grid-cols-3 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500 gap-px text-center mt-2">
                  <span>Black on white</span>
                  <span>Amber on white</span>
                  <span>Favicon test</span>
                </div>
              </div>

              <div className="lg:col-span-2 flex flex-col gap-3 justify-start">
                <a
                  href={`mailto:admin@motoinsights.in?subject=TMG%20logo%20pick%3A%20Option%20${opt.id}%20-%20${encodeURIComponent(opt.title)}&body=Locking%20in%20Option%20${opt.id}%20%E2%80%94%20${encodeURIComponent(opt.title)}.%20Roll%20it%20out%20across%20site%2C%20PDFs%2C%20favicon%2C%20OG%20image.`}
                  data-testid={`pick-option-${opt.key}`}
                  className="inline-flex items-center justify-center gap-2 border border-amber bg-amber px-4 py-3 text-[11px] font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors"
                >
                  <Check className="w-3.5 h-3.5" />
                  Pick option {opt.id}
                </a>
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500 text-center">
                  Locks in variant A · B
                </span>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Recommendation */}
      <section className="px-6 md:px-12 lg:px-16 py-16 border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-3">
              Our recommendation
            </div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              <span className="text-amber">Option 01 · Star horizontal</span>{' '}
              in the all-white variant.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-zinc-400 text-sm md:text-base leading-relaxed">
              The all-white version reads most sophisticated on the site&apos;s
              dark palette — the amber is preserved as an accent colour used
              elsewhere (buttons, stats, dividers), which keeps it precious.
              The all-amber option feels more like a stamp; use that variant
              inside the PDF cover pages and OG image where you want the logo
              to command more attention.
            </p>
            <p className="mt-4 text-zinc-500 text-xs leading-relaxed">
              Once you pick one, I&apos;ll export the pack (PNG, JPG, SVG, square
              monogram, favicon set), roll it across the header, footer, PDFs
              and favicon, and refresh the ZIP in{' '}
              <Link to="/links" className="text-amber underline">/links</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandLogosV2;
